import { Component, HostListener, Input, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { CdkStepper, CdkStepperModule } from '@angular/cdk/stepper';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { invert } from 'lodash-es';
import { PaymentMethodMode } from 'features/customer/customer.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Directionality } from '@angular/cdk/bidi';
import { ChangeDetectorRef } from '@angular/core';
import { SummaryComponent } from '../summary/summary.component';
import { TransactionsService } from 'features/transactions/services/transactions.service';
import { PaymentType } from '../../../payment.model';

const paymentCollectSteps: {[key:string]: number} = {
  'customer': 0,
  'payment': 1,
  'invoice': 2,
  'summary': 3
};

@Component({
  selector: 'app-payment-collect-stepper',
  standalone: true,
  templateUrl: './stepper.component.html',
  host: {
    'class': 'app-payment-collect-stepper',
  },
  providers: [ { provide: CdkStepper, useExisting: PaymentCollectStepperComponent } ],
  imports: [
    NgTemplateOutlet,
    CdkStepperModule,
    MatButton,
    MatIcon,
    NgClass,
    ReactiveFormsModule
  ],
})
export class PaymentCollectStepperComponent extends CdkStepper implements OnInit, OnDestroy {
  @ViewChild('nextButton', {read: MatButton, static: false}) nextButton : MatButton;
  @ViewChild('prevButton', {read: MatButton, static: false}) prefButton : MatButton;
  @Input() set selectedStep(value: string) {
    if (paymentCollectSteps[value] !== undefined) {
      this.selectedIndex = paymentCollectSteps[value];
    }
  }
  @Input() allowNext$ = false;
  @HostListener('document:keydown.enter', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    event.preventDefault();
    this.nextButton._elementRef.nativeElement.click();
  }

  @Input() form = new UntypedFormGroup({});

  subscription = new Subscription();

  @ViewChild(SummaryComponent) summaryComponent: SummaryComponent;

  transactionDetails: any = null;

  private paymentType: PaymentType = PaymentType.SALE;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    _dir: Directionality,
    _changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    private transactionsService: TransactionsService

  ) {
    super(_dir, _changeDetectorRef, elementRef);
  }

  get isFlashPay(): boolean  {
    const formData = this.form?.getRawValue();
    const paymentMode = formData?.payment?.paymentMode;
    if (paymentMode == PaymentMethodMode.flashpay) {
      return true;
    }
    return false;
  }

  get isOneTimePay(): boolean {
    return this.form?.getRawValue()?.payment?.paymentMode === PaymentMethodMode.onetimepay;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.route.queryParams.subscribe(params => {
        const view = params['view'];
        const transactionId = params['transactionId'];

        // Determine payment type
        if (view === 'completeAuth') {
          this.paymentType = PaymentType.COMPLETE_AUTH;
        } else if (view === 'refund') {
          this.paymentType = PaymentType.REFUND;
        } else if (view === 'adjust') {
          this.paymentType = PaymentType.ADJUST;
        } else if (this.form?.getRawValue()?.invoice?.enablePreauth) {
          this.paymentType = PaymentType.PREAUTH;
        } else {
          this.paymentType = PaymentType.SALE;
        }

        // Set initial step based on payment type
        if ([PaymentType.COMPLETE_AUTH, PaymentType.REFUND, PaymentType.ADJUST].includes(this.paymentType)) {
          this.selectedIndex = paymentCollectSteps['invoice'];
          // Safely mark previous steps as completed
          if (this.steps?.first) {
            this.steps.first.completed = true;
          }
          const stepsArray = this.steps?.toArray();
          if (stepsArray && stepsArray[1]) {
            stepsArray[1].completed = true;
          }
        } else if (view) {
          this.selectedStep = view;
        }

        // Load transaction details if needed
        if (transactionId && [PaymentType.COMPLETE_AUTH, PaymentType.REFUND, PaymentType.ADJUST].includes(this.paymentType)) {
          this.loadTransactionDetails(transactionId);
        }
      })
    );
  }

  private loadTransactionDetails(transactionId: string): void {
    this.transactionsService.getTransaction(transactionId).subscribe({
      next: (details) => {
        this.transactionDetails = details;
        // Populate customer and payment details from transaction
        this.form.patchValue({
          customer: details.customerDetails,
          payment: details.paymentDetails
        });
      },
      error: (error) => console.error('Error loading transaction details:', error)
    });
  }

  isNextButtonAllowed(): boolean {
    let currentFormName: string;
    
    // if index is 2 then we are on invoice or completeAuth - update
    if (this.selectedIndex === 2) {
      if (this.route.snapshot.queryParams['view'] === 'completeAuth') {
        currentFormName = 'completeAuth';
      } else if (this.route.snapshot.queryParams['view'] === 'refund') {
        currentFormName = 'refund';
      } else if (this.route.snapshot.queryParams['view'] === 'adjust') {
        currentFormName = 'adjust';
      } else if (this.form.get('invoice')?.get('enablePreauth')?.value) {
        currentFormName = 'preAuth';
      } else if (this.isOneTimePay) {
        // Add this check for one-time payment on invoice step
        const isFormValid = !!this.form.get('invoice')?.valid;
        return isFormValid && this.allowNext$;
      } else {
        currentFormName = 'invoice';
      }
    } else {
      currentFormName = invert(paymentCollectSteps)[this.selectedIndex];
    }

    // Check form validity based on the view type
    if (this.route.snapshot.queryParams['view'] === 'completeAuth') {
      if (currentFormName === 'completeAuth') {
        const isFormValid = !!this.form.get('invoice')?.valid;

        return isFormValid;
      }
      return true;
    }

    // Check form validity based on the view type
    if (this.route.snapshot.queryParams['view'] === 'refund') {
      if (currentFormName === 'refund') {
        const isFormValid = !!this.form.get('invoice')?.valid;

        return isFormValid;
      }
      return true;
    }

    if (this.route.snapshot.queryParams['view'] === 'adjust') {
      if (currentFormName === 'adjust') {
        const isFormValid = !!this.form.get('invoice')?.valid;

        return isFormValid;
      }
      return true;
    }

    if (this.isOneTimePay) {
      const paymentForm = this.form.get('payment')?.value;
      const isFormValid = paymentForm?.paymentMethod && 
                         paymentForm?.oneTimePaymentMethod;

      return isFormValid && this.allowNext$;
    }

    const isFormValid = !!this.form.get(currentFormName)?.valid;


    return isFormValid && this.allowNext$;
  }

  selectStepByIndex(index: number): void {
    this.selectedIndex = index;
  }

  override next(): void {
    const currentFormName = invert(paymentCollectSteps)[this.selectedIndex];
    
    const formData = this.form?.getRawValue();

    if ((this.route.snapshot.queryParams['view'] === 'completeAuth' || 
         this.route.snapshot.queryParams['view'] === 'refund' ||
         this.route.snapshot.queryParams['view'] === 'adjust') && 
         currentFormName === 'customer') {
      super.next();
      return;
    }

    // Check if we're on step 2 set view for summary page
    if (this.selectedIndex === 1) {
      if ( formData?.payment?.paymentMode === PaymentMethodMode.flashpay) {
        this.router.navigate(['/payment/collect'], {
          queryParams: { view: 'flashpay' },
          queryParamsHandling: 'merge'
        });
      }

      if ( formData?.payment?.paymentMode === PaymentMethodMode.achpay) {
        this.router.navigate(['/payment/collect'], {
          queryParams: { view: 'achpay' },
          queryParamsHandling: 'merge'
        });
      }

      this.form.get('invoice')?.patchValue({ enablePreauth: false });
      super.next();
      return;
    }

    // Check if we're on step 2 set view for summary page
    if (this.selectedIndex === 2 && formData?.invoice?.enablePreauth) {
      this.router.navigate(['/payment/collect'], {
        queryParams: { view: 'preAuth' },
        queryParamsHandling: 'merge'
      });
      super.next();
      return;
    }


    
    if (this.selectedIndex === 2 && (this.route.snapshot.queryParams['view'] != 'preAuth' && this.route.snapshot.queryParams['view'] != 'completeAuth' && this.route.snapshot.queryParams['view'] != 'refund' && this.route.snapshot.queryParams['view'] != 'adjust' && this.route.snapshot.queryParams['view'] != 'flashpay' && this.route.snapshot.queryParams['view'] != 'onetimepay')) {
      this.router.navigate(['/payment/collect'], {
        queryParams: { view: 'invoice' },
        queryParamsHandling: 'merge'
      }).then(() => {
        if (this.isNextButtonAllowed()) {
          super.next();
          this.onStepChange({ selectedIndex: this.selectedIndex });
        }
      });
      return;
    }

    if (this.isNextButtonAllowed()) {
      super.next();
      this.onStepChange({ selectedIndex: this.selectedIndex });
    }
  }

  override previous(): void {

    // // clear view param if onetimepay is set and we are on payment step
    // if (this.isOneTimePay) {
    //   this.router.navigate(['/payment/collect'], {
    //     queryParams: { view: null },
    //     replaceUrl: true
    //   }).then(() => super.previous());

    //   // need to set onetimepay to false
    //   this.form.get('payment')?.patchValue({ paymentMode: PaymentMethodMode.creditcard });

    //   return;
    // }

    // Prevent going back from invoice step for completeAuth and refund
    if (this.selectedIndex === paymentCollectSteps['invoice'] && 
        [PaymentType.COMPLETE_AUTH, PaymentType.REFUND, PaymentType.ADJUST].includes(this.paymentType)) {
      return;
    }

    // Handle other previous navigation
    if (this.selectedIndex === paymentCollectSteps['invoice'] && !this.isOneTimePay) {
      const currentParams = { ...this.route.snapshot.queryParams };
      delete currentParams['view'];
      
      this.router.navigate(['/payment/collect'], {
        queryParams: currentParams,
        replaceUrl: true
      }).then(() => super.previous());
    return;
    }

    if (this.selectedIndex === paymentCollectSteps['summary'] && 
        (this.route.snapshot.queryParams['view'] === 'preAuth' || 
         this.route.snapshot.queryParams['view'] === 'invoice')) {
      const currentParams = { ...this.route.snapshot.queryParams };
      delete currentParams['view'];
      
      
      this.router.navigate(['/payment/collect'], {
        queryParams: currentParams,
        replaceUrl: true
      }).then(() => super.previous());
    return;
    }


    // go back to previous step
    super.previous();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscription.unsubscribe();
  }

  onStepChange(event: any): void {
    // If moving to summary step
    if (event.selectedIndex === 3) {
      //console.log('Stepped to summary');
      setTimeout(() => {
          //console.log('Refreshing summary');
          this.refreshTransactionDetails();
      }, 0);
    }

  }

  private refreshTransactionDetails(): void {
    const view = this.route.snapshot.queryParams['view'];
    const transactionId = this.route.snapshot.queryParams['transactionId'];

    // console.log('Loading transaction details...');
    // console.log('View:', view);
    // console.log('TransactionId:', transactionId);

    if ((view === 'refund' || view === 'completeAuth' || view === 'adjust') && transactionId) {
      //console.log('Fetching transaction details...');
      this.transactionsService.getTransaction(transactionId).subscribe({
        next: (details) => {
          //console.log('Transaction details received:', details);
          this.transactionDetails = details;
          
          //console.log('Transaction Details:', this.transactionDetails);
          //console.log('Form Data:', this.form?.getRawValue());
        },
        error: (error) => {
          console.error('Error fetching transaction details:', error);
        }
      });
    }
  }

  shouldShowPreviousButton(): boolean {
    // Hide button on invoice step for completeAuth and refund
    if (this.selectedIndex === paymentCollectSteps['invoice'] && 
        [PaymentType.COMPLETE_AUTH, PaymentType.REFUND, PaymentType.ADJUST].includes(this.paymentType)) {
      return false;
    }

    if (this.selectedIndex === paymentCollectSteps['payment'] && this.isOneTimePay) {
      return false;
    }

    // Show button if not on first step
    return this.selectedIndex > 0;
  }

}
